/**
 * 运行上下文：每次发送告警时快照一份，
 * 用于回答「谁、在哪个页面、什么浏览器、哪次发版」。
 */
import type { MonitorContext } from '@/types/monitor';

/** 会话 ID：页面加载时生成一次，用于串联同一次访问内的多条告警 */
const SESSION_ID = Math.random().toString(36).slice(2, 10);

/** 浏览器识别规则，自上而下首个命中者生效（Edge/Opera 必须排在 Chrome 前） */
const BROWSER_RULES: Array<[RegExp, string]> = [
  [/Edg\/(\d+)/, 'Edge'],
  [/OPR\/(\d+)/, 'Opera'],
  [/Chrome\/(\d+)/, 'Chrome'],
  [/Firefox\/(\d+)/, 'Firefox'],
  [/Version\/(\d+).*Safari/, 'Safari'],
];

/** 操作系统识别规则，自上而下首个命中者生效 */
const OS_RULES: Array<[RegExp, string]> = [
  [/Windows NT/, 'Windows'],
  [/Mac OS X/, 'macOS'],
  [/Android/, 'Android'],
  [/iPhone|iPad/, 'iOS'],
  [/Linux/, 'Linux'],
];

/** 路由取值由 index.ts 注入，避免 monitor 直接依赖 router 实例造成循环引用 */
let readRoute: () => string = () => '';

export const setRouteReader = (fn: () => string): void => {
  readRoute = fn;
};

/** 把 UA 串压成「Chrome 131 / Windows」这种一眼能看懂的形式 */
const describeUa = (ua: string): string => {
  let browser = '';
  for (const [re, name] of BROWSER_RULES) {
    const matched = re.exec(ua);
    if (matched) {
      browser = `${name} ${matched[1]}`;
      break;
    }
  }
  let os = '';
  for (const [re, name] of OS_RULES) {
    if (re.test(ua)) {
      os = name;
      break;
    }
  }
  return `${browser || '未知浏览器'} / ${os || '未知系统'}`;
};

/**
 * 取当前登录用户描述。
 * 直接读 localStorage 而不用 useUserStore：监控可能在 pinia 就绪前就捕获到错误，
 * 此时调用 store 会抛异常。localStorage 的 'user' key 与 stores/user.ts 保持一致。
 */
const describeUser = (): string => {
  /** 有意例外（CLAUDE.md「非必要不加 try/catch」）：取不到用户不能让上报中断 */
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '未登录';
    const user = JSON.parse(raw) as {
      username?: string;
      account_id?: number;
      agent_id?: number;
    };
    if (!user.username) return '未登录';
    const id = user.account_id ?? user.agent_id;
    return id ? `${user.username} (#${id})` : user.username;
  } catch {
    return '未知';
  }
};

export const buildContext = (): MonitorContext => ({
  /** 未配置时落「未标识」而不是静默留空，让漏配一眼可见 */
  app: import.meta.env.VITE_MONITOR_APP || '未标识',
  env: import.meta.env.MODE,
  sessionId: SESSION_ID,
  route: readRoute(),
  user: describeUser(),
  ua: describeUa(navigator.userAgent),
  url: location.href,
  version: __APP_BUILD_TIME__,
});
